import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

/**
 * /legal/privacy — Public privacy notice.
 *
 * Written as an honest statement of the current data-handling posture of the
 * ImpactVest preview platform. Nothing here is a claim about capabilities
 * that have not shipped. Where a capability is "planned" the copy says so.
 */

const LAST_UPDATED = "3 August 2026";

export default function Privacy() {
  useEffect(() => { document.title = "Privacy Notice | ImpactVest"; window.scrollTo(0, 0); }, []);
  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-36 pb-24" data-testid="page-privacy">
        <div className="iv-container max-w-3xl">
          <div className="iv-eyebrow mb-6">Privacy Notice</div>
          <h1 className="font-serif-display text-[42px] md:text-[56px] leading-[1.02] tracking-tight text-iv-fg">
            Privacy Notice.
          </h1>
          <p className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
            Last updated: {LAST_UPDATED} · Applies to impactvest.com and the ImpactVest Test Workspace
          </p>

          <Section title="1. Who we are">
            <p>ImpactVest is a product of Gaiosis Systems. References to "we", "us" and "our" in this
            Notice refer to Gaiosis Systems as the data controller for personal data processed via
            impactvest.com and the ImpactVest Test Workspace.</p>
            <p>Contact for privacy enquiries: <a className="iv-link-underline" href="mailto:office@impactvest.com">office@impactvest.com</a>.</p>
          </Section>

          <Section title="2. What we collect">
            <ul>
              <li><b>Registration data</b> — full name, professional email, organisation, job title, role, organisation type, country, chosen pathway (investor or company) and the three consents you gave on the registration form.</li>
              <li><b>Session data</b> — a single-use email verification link and, after verification, a signed session token stored client-side. We do not use third-party tracking cookies.</li>
              <li><b>Product-usage events</b> — pages viewed inside the Test Workspace, journey step completions, feedback you submit. Used to improve the product; never sold.</li>
              <li><b>Documents you upload</b> — only in a live private workspace, only from you, only in scope of the analysis you asked for.</li>
            </ul>
          </Section>

          <Section title="3. What we do NOT collect">
            <ul>
              <li>Passwords for the Test Workspace (magic-link sign-in only).</li>
              <li>Payment card details (Stripe processes cards; card data never touches our servers).</li>
              <li>Location / device fingerprinting beyond standard HTTPS request metadata.</li>
            </ul>
          </Section>

          <Section title="4. Purposes and legal bases (GDPR)">
            <ul>
              <li><b>Delivering the service you asked for</b> — Article 6(1)(b), performance of contract.</li>
              <li><b>Product improvement analytics</b> — Article 6(1)(f), our legitimate interest, balanced against your rights. You may object at any time.</li>
              <li><b>Consent-recorded processing</b> — Article 6(1)(a), for the three explicit consents on the registration form.</li>
            </ul>
          </Section>

          <Section title="5. Retention">
            <p>Registration and session records are retained for the lifetime of your workspace plus 24 months, unless you request earlier deletion. Uploaded documents in live workspaces are governed by the customer-controlled retention model described in the <a className="iv-link-underline" href="/legal/retention">Data retention notice</a>: they are retained until you delete the workspace, at which point every document, extract, claim, revision and released pack version is removed. Consent and deletion audit records (content-free) are retained indefinitely as legal evidence.</p>
          </Section>

          <Section title="6. Sub-processors">
            <ul>
              <li><b>Emergent</b> — hosting, deployment, environment variable storage (secrets never leave the platform).</li>
              <li><b>Stripe</b> — payment processing (customer-facing DBA: Gaiosis Systems). Live payments are currently disabled.</li>
              <li><b>Resend</b> — transactional email delivery (magic-link sign-in emails).</li>
              <li><b>OpenAI / GPT-5</b> — evidence-extraction language-model calls, invoked only against documents you upload to a live workspace. No inputs are used to train third-party models.</li>
            </ul>
          </Section>

          <Section title="7. Your rights">
            <p>You have the rights of access, rectification, erasure, portability and objection under GDPR and equivalent rights under UK, US-state and other applicable privacy law. Email <a className="iv-link-underline" href="mailto:office@impactvest.com">office@impactvest.com</a> to exercise any of these. We respond within 30 days.</p>
          </Section>

          <Section title="8. Changes to this Notice">
            <p>Material changes will be posted here and communicated to registered users by email. The
            "Last updated" date above indicates the current effective version.</p>
          </Section>

          <div className="mt-16 pt-8 border-t border-iv-line flex items-center justify-between gap-4 flex-wrap">
            <Link to="/legal/terms" className="iv-link-underline text-[13.5px] text-iv-fg-2">Read the Terms of Use →</Link>
            <Link to="/contact" className="iv-link-underline text-[13.5px] text-iv-fg-2">Contact us →</Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12 pt-8 border-t border-iv-line">
      <h2 className="font-serif-display text-[24px] md:text-[28px] leading-tight text-iv-fg mb-4">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-iv-fg-2 [&_ul]:list-disc [&_ul]:ml-6 [&_li]:mt-2">{children}</div>
    </section>
  );
}
